import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { API } from "aws-amplify";
import {
  Box,
  Button,
  InputLabel,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { DialogWindow } from "UI/DialogWindow";
import { FormTextField } from "UI/FormTextField";
import { FormSelectField } from "UI/FormSelectField";
import { phoneNumberValidation, required, zipCodeValidation } from "models/validations";
import { states } from "models/states";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { FormMaskedTextField } from "UI/FormMaskedTextField";
import { AddNewRegionDialog } from "./AddNewRegionDialog";
import { RequiredMark } from "UI/RequiredMark";
import { useCloseAndReset } from "hooks/useCloseAndReset";
import { Captcha } from "./Captcha";

/** @type {import("types/commonTypes").NewOrganizationData}*/
const defaultFormData = {
  name: "",
  street: "",
  street2: "",
  city: "",
  state: "",
  zipCode: "",
  phone: "",
  email: "",
  speciality: "",
  website: "",
  additionalNotes: "",
  code: "",
  captchaId: "",
};

export const AddNewPracticeDialog = ({
  open,
  handleClose,
  refreshList = () => null,
  regionsOptions = {},
  refreshRegionsOptions = () => null,
  setCreatedPractice = (value) => null,
  isPublic = false,
}) => {
  const { createApiCall } = useCreateApiCall();
  const dispatch = useAppDispatch();
  const spinner = useAppSelector((state) => !!state.spinner);
  const [isAddNewRegionDialogOpen, setIsAddNewRegionDialogOpen] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    setError,
    formState: { isDirty },
  } = useForm({
    defaultValues: defaultFormData,
  });

  const forceCloseAndReset = () => {
    reset(defaultFormData);
    handleClose();
  };

  const onSubmit = createApiCall(async () => {
    setIsLoading(true);
    const organization = getValues();
    if (!isPublic) {
      delete organization.code;
      delete organization.captchaId;
    }

    let result;

    if (isPublic) {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/practice`,
        {
          method: "PUT",
          body: JSON.stringify({
            organization,
          }),
        }
      );
      result = await response.json();
    } else {
      result = await API.put("restApi", "/organizations", {
        body: {
          organization,
        },
      });
    }

    if (result?.error) {
      if (result?.error === "Invalid code") {
        setError("code", {
          message: "Invalid code",
        });
        setIsLoading(false);
        return;
      }
      dispatch(showError(result?.error));
      setIsLoading(false);
      return;
    }

    if (result?.id) {
      const { name, phone, speciality } = organization;
      const newPractice = {
        id: result.id ?? "",
        name: [name, speciality, phone].filter(Boolean).join(", "),
      };
      setCreatedPractice(newPractice);
    }

    setIsLoading(false);
    refreshList();
    forceCloseAndReset();
  });

  const closeAndReset = useCloseAndReset(forceCloseAndReset, isDirty);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isDisabled = spinner || isLoading;

  return (
    <DialogWindow
      open={open}
      withCloseButton
      handleClose={closeAndReset}
      title={"Add New Practice"}
      content={
        <>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack direction={fullScreen ? "column" : "row"} gap={3} mt={2}>
              <Stack width="100%" gap={1}>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="name">
                    Practice Name {!isPublic && <RequiredMark />}
                  </InputLabel>
                  <FormTextField
                    id="name"
                    name="name"
                    control={control}
                    rules={{ required }}
                    InputProps={{
                      autoComplete: "off",
                      disabled: isDisabled,
                    }}
                  />
                </Stack>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="street">
                    Address Line 1 {!isPublic && <RequiredMark />}
                  </InputLabel>
                  <FormTextField
                    id="street"
                    name="street"
                    control={control}
                    rules={{ required }}
                    InputProps={{
                      autoComplete: "off",
                      disabled: isDisabled,
                    }}
                  />
                </Stack>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="street22">
                    Address Line 2 {isPublic && "(optional)"}
                  </InputLabel>
                  <FormTextField
                    id="stree2"
                    name="street2"
                    control={control}
                    InputProps={{
                      autoComplete: "off",
                      disabled: isDisabled,
                    }}
                  />
                </Stack>
                <Stack direction={"row"} gap={2}>
                  <Stack alignItems={"flex-start"}>
                    <InputLabel htmlFor="city">
                      City {!isPublic && <RequiredMark />}
                    </InputLabel>
                    <FormTextField
                      id="city"
                      name="city"
                      control={control}
                      rules={{ required }}
                      InputProps={{
                        autoComplete: "off",
                        disabled: isDisabled,
                      }}
                    />
                  </Stack>
                  <Stack
                    alignItems={"flex-start"}
                    minWidth={"30%"}
                    maxWidth={"35%"}
                  >
                    <InputLabel htmlFor="state">
                      State {!isPublic && <RequiredMark />}
                    </InputLabel>
                    <FormSelectField
                      id="state"
                      name="state"
                      control={control}
                      options={states}
                      rules={{ required }}
                      withNone={false}
                      InputProps={{
                        disabled: isDisabled,
                      }}
                    />
                  </Stack>
                  <Stack alignItems={"flex-start"}>
                    <InputLabel htmlFor="zipCode">
                      Zip Code {!isPublic && <RequiredMark />}
                    </InputLabel>
                    <FormTextField
                      id="zipCode"
                      name="zipCode"
                      control={control}
                      rules={zipCodeValidation}
                      InputProps={{
                        autoComplete: "off",
                        disabled: isDisabled,
                      }}
                    />
                  </Stack>
                </Stack>
              </Stack>
              <Stack width="100%" gap={1}>
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="phone">
                    Main Phone {!isPublic && <RequiredMark />}
                  </InputLabel>
                  <FormMaskedTextField
                    id="phone"
                    name="phone"
                    control={control}
                    rules={phoneNumberValidation}
                    mask="999-999-9999"
                    InputProps={{
                      autoComplete: "off",
                      disabled: isDisabled,
                    }}
                  />
                </Stack>
                {/* <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="email">
                    Email contact
                  </InputLabel>
                  <FormTextField
                    id="email"
                    name="email"
                    control={control}
                    rules={notRequiredEmailValidation}
                    InputProps={{
                      autoComplete: "off",
                    }}
                  />
                </Stack> */}
                {/* <Stack
                  direction={"row"}
                  gap={2}
                  justifyContent={"space-between"}
                > */}
                <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="speciality">
                    Speciality {isPublic && "(optional)"}
                  </InputLabel>
                  <FormTextField
                    id="speciality"
                    name="speciality"
                    control={control}
                    InputProps={{
                      autoComplete: "off",
                      disabled: isDisabled,
                    }}
                  />
                </Stack>
                <Stack alignItems={"flex-start"} flex="1">
                  <InputLabel htmlFor="website">
                    Website {isPublic && "(optional)"}
                  </InputLabel>
                  <FormTextField
                    id="website"
                    name="website"
                    control={control}
                    InputProps={{
                      autoComplete: "off",
                      disabled: isDisabled,
                    }}
                  />
                </Stack>
                {/* </Stack> */}
                {isPublic ? (
                  <Captcha control={control} setValue={setValue} />
                ) : (
                  <Stack direction={"row"} gap={2} alignItems={"flex-end"}>
                    <Stack alignItems={"flex-start"} flex="1">
                      <InputLabel htmlFor="marketingRegionId">
                        Marketing Region
                      </InputLabel>
                      <FormSelectField
                        id="marketingRegionId"
                        name="marketingRegionId"
                        control={control}
                        options={regionsOptions}
                        InputProps={{
                          disabled: isDisabled,
                        }}
                      />
                    </Stack>
                    <Button
                      sx={{ padding: "10px" }}
                      onClick={() => setIsAddNewRegionDialogOpen(true)}
                      disabled={isDisabled}
                    >
                      Add new region
                    </Button>
                  </Stack>
                )}
                {/* <Stack alignItems={"flex-start"}>
                  <InputLabel htmlFor="additionalNotes">
                    Additional notes
                  </InputLabel>
                  <FormTextField
                    id="additionalNotes"
                    name="additionalNotes"
                    control={control}
                  />
                </Stack> */}
              </Stack>
            </Stack>
          </form>
          <AddNewRegionDialog
            open={isAddNewRegionDialogOpen}
            handleClose={() => setIsAddNewRegionDialogOpen(false)}
            refreshList={refreshRegionsOptions}
          />
        </>
      }
      actions={
        <Stack
          direction={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"100%"}
          gap={2}
          mb={2}
        >
          {!isPublic && (
            <Box ml={2} sx={{ color: "gray", fontSize: 14 }}>
              <RequiredMark /> These fields are required
            </Box>
          )}
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={isDisabled}
          >
            Add New Practice
          </Button>
        </Stack>
      }
    />
  );
};
